import axios from 'axios';

import { sortOptionsFromApi } from '@utils/FilterSettings';
import {
  Country,
  CountryFilterPair,
  CountryMasterPgsFilterPair,
  Option,
  ProductGroupPriceBandValue,
} from '@utils/pageConfig/Types';
import { environment } from '@environments/environment';
import { paramsSerializer } from './utils/OverviewServiceUtils';
import { ProductDataType, ProductType } from '@pages/product-analyzer/Product';
import { getQueryClient } from '../queries/QueryClientWrapper';

export interface PricebandByPGListParam {
  clientId: number;
  country: string[];
  productType: ProductType[];
}

/*
 * function that gets the product groups of a given retailer+country+periodType
 */

export const getProductGroupsByCountry = async (
  clientId: number,
  countries: Option[] | Country[],
  productType?: ProductType,
  productDataType?: ProductDataType,
  includeMissingPgsInMasterRecord: boolean = false,
): Promise<CountryFilterPair[]> => {
  try {
    const qc = getQueryClient('product-groups');

    const countryIds = countries
      .map((c: { label: string; value: string } | Country) =>
        'value' in c ? c.value.toUpperCase().trim() : String(c.id).toUpperCase().trim(),
      )
      .join('&country=');

    const resolvedProductType = productType ?? ProductType.PerformanceAnalyzer;
    const resolvedProductDataType = productDataType ?? ProductDataType.PerformanceAnalyzer;

    const params = {
      clientId,
      country: countryIds,
      productType: resolvedProductType,
      productDataType: resolvedProductDataType,
      includeMissingPgsInMasterRecord,
    };

    const queryKey = ['product-groups-by-country', params] as const;

    return await qc.ensureQueryData<CountryFilterPair[]>({
      queryKey,
      queryFn: async ({ signal }) => {
        const { data } = await axios.get<CountryFilterPair[]>(`${environment.baseUrl}/v2/filters/product-group`, {
          params,
          paramsSerializer,
          signal,
        });
        data.forEach((r) => (r.filter.options = sortOptionsFromApi(r.filter.options)));
        return data;
      },
      retry: false,
    });
  } catch (err: any) {
    throw new Error(`An error has occurred: ${err?.message ?? err}`);
  }
};

export const getProductGroupsMasterByCountry = async (
  clientId: number,
  countries: Option[] | Country[],
  productType?: ProductType,
  productDataType?: ProductDataType,
): Promise<CountryMasterPgsFilterPair[]> => {
  const qc = getQueryClient('product-groups');
try {
  return await qc.ensureQueryData({
    queryKey: ['product-groups-master-by-country', { clientId, countries, productType, productDataType }],
    queryFn: async () => {
      const countryIds = countries
        .map((country: { label: string; value: string } | Country) => {
          if ('value' in country) {
            return country.value.toUpperCase();
          } else {
            return country.id.toUpperCase();
          }
        })
        .join('&country=');

    const params = {
      clientId: clientId,
      country: countryIds,
      productType: productType ?? ProductType.PerformanceAnalyzer,
      productDataType: productDataType ?? ProductDataType.PerformanceAnalyzer,
    };

      const { data } = await axios.get<CountryMasterPgsFilterPair[]>(
        `${environment.baseUrl}/opa/v2/pgs-by-focus-period`,
        {
          params: params,
          paramsSerializer: paramsSerializer,
        },
      );

      return data;
    },
    retry: false,
  });
} catch (err: any) {
  throw new Error(`An error has occurred: ${err}`);
}
};

export const getProductGroupsPriceBand = async (
  clientId: number,
  countries: string[],
  productType?: ProductType,
  productDataType?: ProductDataType,
): Promise<ProductGroupPriceBandValue[]> => {
  try {
    const countryIds = countries
      .map((country) => {
        return country.toUpperCase();
      })
      .join('&country=');

    const params = {
      clientId: clientId,
      country: countryIds,
      productType: productType ?? ProductType.PerformanceAnalyzer,
      productDataType: productDataType ?? ProductDataType.PerformanceAnalyzer,
    };

    const { data } = await axios.get<ProductGroupPriceBandValue[]>(
      `${environment.baseUrl}/v2/filters/product-group-price-band-list`,
      {
        params: params,
        paramsSerializer: paramsSerializer,
      },
    );

    return data;
  } catch (err: any) {
    throw new Error(`An error has occurred: ${err}`);
  }
};
